import React, { useEffect, useMemo, forwardRef } from "react";

import useLanguage from "../../hooks/useLanguage";
import { varifyColumns } from "./utils";
import placeholder from "./cells/placeholder.js";

import Cell from "./cells/Cell";
import Tags from "./cells/Tags";
import Actions from "./cells/Actions";
import DateCell from "./cells/Date";
import Redirection from "./cells/Hyperlink.jsx";
import NumberCell from "./cells/Number.jsx";
import Link from "./cells/Link.jsx";
import Select from "./cells/Select.jsx";

import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import {
  EmptyStateWrapper,
  FixedTable,
  HeaderTableCell,
  StyledTableCell,
  StyledTableRow,
  StyledTableVirtuoso,
  TablePaper,
} from "./style.jsx";
import Toggle from "./cells/Toggle.jsx";
import { Typography } from "@mui/material";
import Map from "./cells/Map.jsx";
import Checkbox from "./cells/Checkbox.jsx";

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer component={TablePaper} {...props} ref={ref} />
  )),
  Table: (props) => <FixedTable {...props} />,
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow: forwardRef((props, ref) => <StyledTableRow {...props} ref={ref} />),
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

function fixedHeaderContent(columns, code, isRtl) {
  if (!varifyColumns(columns)) return <></>;
  return (
    <StyledTableRow>
      {columns?.[code]?.map((column) => (
        <HeaderTableCell
          key={column.dataKey}
          variant="head"
          align={
            column.align ||
            (column.numeric
              ? "right"
              : column.type === "checkbox" ||
                  column.type === "actions" ||
                  column.type === "map"
                ? "center"
                : isRtl
                  ? "right"
                  : "left")
          }
          style={{ width: column.width }}
        >
          {column.label}
        </HeaderTableCell>
      ))}
    </StyledTableRow>
  );
}

function rowContentFactory(_index, columns, row, code, isData, actions, columnHandlers, isRtl) {
  const textAlign = isRtl ? "right" : "left";
  return (
    <>
      {columns?.[code]?.map((column) => {
        switch (column.type) {
          case "tags":
            return (
              <Tags
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
                style={{ width: `${column.width}px` }}
              />
            );
          case "link":
            return (
              <Link
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
              />
            );
          case "redirection":
            return (
              <Redirection
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
                href={
                  row.source === "reframer"
                    ? column.reframerHref || "/player"
                    : column.href
                }
                disableWrap={column.disableWrap}
                enableCopy={column.enableCopy}
              />
            );
          case "actions":
            if (!isData && !actions)
              return (
                <StyledTableCell
                  key={column.dataKey}
                  width={`${column.width}px`}
                />
              );
            return (
              <Actions
                id={row.id}
                key={column.dataKey}
                style={{ width: `${column.width}px` }}
                components={typeof actions === 'function' ? actions(row) : actions}
              />
            );
          case "toggle":
            return (
              <Toggle
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
                onClick={() => column.onClick(row.id)}
                style={{ width: `${column.width}px` }}
              />
            );
          case "checkbox": {
            const handler = columnHandlers?.[column.dataKey];
            return (
              <Checkbox
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
                onChange={handler?.onChange || column.onChange}
                disabled={
                  typeof handler?.disabled === "function"
                    ? handler.disabled(row)
                    : column.disabled
                }
                tooltipOn={handler?.tooltipOn || column.tooltipOn}
                tooltipOff={handler?.tooltipOff || column.tooltipOff}
                style={{ width: `${column.width}px` }}
              />
            );
          }
          case "number":
            return (
              <NumberCell
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
              />
            );
          case "date":
          case "time":
            return (
              <DateCell
                id={row.id}
                key={column.dataKey}
                content={row[column.dataKey]}
                style={{ width: `${column.width}px` }}
              />
            );
          case "map":
            return (
              <Map
                id={row.id}
                key={column.dataKey}
                align={column.numeric ? "right" : textAlign}
                content={row[column.dataKey]}
                hashmap={column.hashmap}
                style={{ width: `${column.width}px` }}
              />
            );
          case "select":
            return <Select key={column.dataKey} />;
          case "cell":
          default:
            return (
              <Cell
                id={row.id}
                key={column.dataKey}
                align={column.numeric ? "right" : textAlign}
                content={row[column.dataKey]}
                style={{ width: `${column.width}px` }}
              />
            );
        }
      })}
    </>
  );
}

export default function VirtualTable({
  height,
  width,
  columns,
  data,
  actions,
  columnHandlers,
  minHeight,
  disableBorder = false,
  // Optional per-table RTL override. When omitted the table follows the app
  // language direction; pass a boolean to force RTL/LTR independently (e.g. a
  // Hebrew-content table shown while the UI language is English).
  rtl,
}) {
  const [
    {
      code,
      direction,
      components: { table },
    },
  ] = useLanguage();
  const isRtl = rtl ?? direction === "rtl";
  const isData = useMemo(() => data?.length > 0, [data]);

  // react-virtuoso paints its rows only after it measures the scroller. When the
  // flex parent sizes after mount, the rows render into the DOM but stay
  // unpainted — the table looks empty on load until a scroll/resize re-measures
  // it. Nudge one re-measure on the next frame after mount/data change.
  useEffect(() => {
    if (!data?.length) return;
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(id);
  }, [data]);

  if (!data || !data.length)
    return (
      <EmptyStateWrapper>
        <Typography variant="body2" color="text.secondary">
          {table.empty}
        </Typography>
      </EmptyStateWrapper>
    );

  return (
    <TablePaper
      style={{
        height: isData ? height || "100%" : "200px",
        minHeight: minHeight || "none",
        width: width || "100%",
      }}
    >
      <StyledTableVirtuoso
        border={!!disableBorder ? undefined : true}
        className={`${isRtl ? "rtl" : ""} i-${+isData}`}
        data={isData ? data : placeholder(columns, code, table.empty)}
        // Render the first screenful on the very first paint. Without this,
        // react-virtuoso mounts with zero painted rows and only fills in after a
        // scroll/resize — so the table looks empty on load until the user scrolls.
        initialItemCount={Math.min(isData ? data.length : 0, 20)}
        components={VirtuosoTableComponents}
        fixedHeaderContent={() => fixedHeaderContent(columns, code, isRtl)}
        itemContent={(idx, row) =>
          rowContentFactory(idx, columns, row, code, isData, actions, columnHandlers, isRtl)
        }
      />
    </TablePaper>
  );
}
